import { checkIsHtmlElement } from "@repo/ui/helpers/dom";
import { EventEmitter } from "eventemitter3";
import {
  useEffect,
  useState,
  type FC,
  type ReactNode,
  type RefObject,
} from "react";
import { searchKeyboardShortcut } from "../search/searchKeyboardShortcut.js";
import { KeyboardShortcutsContext } from "./keyboardShortcutsContext.js";

interface GlobalKeyboardShortcutsProviderProps {
  elementRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export const GlobalKeyboardShortcutsProvider: FC<
  GlobalKeyboardShortcutsProviderProps
> = ({ elementRef, children }) => {
  const keyboardShortcutsEmitter =
    useGlobalKeyboardShortcutsEmitter(elementRef);

  return (
    <KeyboardShortcutsContext.Provider value={keyboardShortcutsEmitter}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

function useGlobalKeyboardShortcutsEmitter(
  elementRef: RefObject<HTMLElement | null>,
) {
  const [keyboardShortcutsEmitter] = useState(() => new EventEmitter());

  useEffect(() => {
    const body = elementRef.current?.ownerDocument.body;
    if (!body) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Don't trigger global shortcuts if we're in a modal
      if (
        checkIsHtmlElement(event.target) &&
        event.target.closest("[role=dialog]")
      ) {
        return;
      }

      // Don't trigger shortcuts if we're focused on an input, textarea, or contenteditable element
      if (
        checkIsHtmlElement(event.target) &&
        event.target.matches("input, textarea, [contenteditable]")
      ) {
        return;
      }

      // Don't trigger if the event was already handled
      if (event.defaultPrevented) {
        return;
      }

      switch (event.key) {
        case "/":
          event.preventDefault();
          keyboardShortcutsEmitter.emit(searchKeyboardShortcut);
          break;

        default:
          break;
      }
    };

    body.addEventListener("keydown", handleKeyDown);

    return () => {
      body.removeEventListener("keydown", handleKeyDown);
    };
  }, [elementRef, keyboardShortcutsEmitter]);

  return keyboardShortcutsEmitter;
}
